/**
 * AnswerComment Component
 *
 * Renders a single answer comment within a forum thread, including options for editing and displaying subcomments.
 *
 * Features:
 * - Displays comment options for admin and regular users
 * - Allows toggling edit mode for the comment
 * - Passes relevant props to child components for comment data and actions
 * - Uses CSS modules for scoped styling
 *
 * @param {Object} props - Component properties
 * @param {Object} [props.answerData] - Data object for the answer comment
 * @param {number} [props.idLesson] - Lesson ID associated with the comment
 * @param {function} [props.showAnswers] - Function to show/hide answers
 * @returns {JSX.Element} Rendered AnswerComment component
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import CommentPost from '../CommentPost/CommentPost';
import CommentOptions from '../CommentOptions/CommentOptions';
import classes from './AnswerComment.module.css';

const AnswerComment = props => {
  const [editComment, setEditComment] = useState(false);

  /**
   * Toggles the edit mode for the comment
   */
  const editCommentHandler = () => {
    setEditComment(!editComment);
  };

  return (
    <div className={classes.answerCommentContainer}>
      {/* Custom component: CommentOptions receives adminComment, idComment, idLesson, subcomment, showAnswers, editCommentHandler */}
      <CommentOptions
        adminComment={props.answerData.admin === 1 ? true : false}
        idComment={props.answerData.idSubcomment}
        idLesson={props.idLesson}
        subcomment
        showAnswers={props.showAnswers}
        editCommentHandler={editCommentHandler}
      />
      {/* Custom component: CommentPost receives commentData, subcomment, idComment, editCommentHandler, editComment */}
      <CommentPost
        commentData={props.answerData}
        subcomment
        idComment={props.answerData.idSubcomment}
        editCommentHandler={editCommentHandler}
        editComment={editComment}
      />
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
AnswerComment.propTypes = {
  /** Data object for the answer comment */
  answerData: PropTypes.object,
  /** Lesson ID associated with the comment */
  idLesson: PropTypes.number,
  /** Function to show/hide answers */
  showAnswers: PropTypes.func
};

AnswerComment.defaultProps = {
  answerData: undefined,
  idLesson: undefined,
  showAnswers: undefined
};

export default AnswerComment;
